import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import './borne-club-v2.css';

const fmtTel = (d) => (d.match(/.{1,2}/g) || []).join(' ');
const isMobile = (d) => d.length === 10 && (d.startsWith('06') || d.startsWith('07'));

const Numpad = ({ onDigit, onBack, onClear, disabled }) => (
  <div className="k2-numpad">
    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
      <button key={n} type="button" className="k2-key" disabled={disabled} onClick={() => onDigit(String(n))} data-testid={`k2-key-${n}`}>{n}</button>
    ))}
    <button type="button" className="k2-key k2-key-fn" disabled={disabled} onClick={onClear} data-testid="k2-key-clear">C</button>
    <button type="button" className="k2-key" disabled={disabled} onClick={() => onDigit('0')} data-testid="k2-key-0">0</button>
    <button type="button" className="k2-key k2-key-fn" disabled={disabled} onClick={onBack} data-testid="k2-key-back">⌫</button>
  </div>
);

const KB_ROWS = [
  ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
  ['w', 'x', 'c', 'v', 'b', 'n', 'é', 'è', 'ç', 'back'],
  ['ë', 'ï', '-', "'", 'space', 'ok'],
];

const Keyboard = ({ onKey, onBack, onDone }) => (
  <div className="k2-kb" data-testid="k2-keyboard">
    {KB_ROWS.map((row, r) => (
      <div key={r} className="k2-kb-row">
        {row.map((k) => {
          if (k === 'back') return <button key={k} type="button" className="k2-kb-key k2-kb-fn" onClick={onBack}>⌫</button>;
          if (k === 'space') return <button key={k} type="button" className="k2-kb-key k2-kb-space" onClick={() => onKey(' ')}>Espace</button>;
          if (k === 'ok') return <button key={k} type="button" className="k2-kb-key k2-kb-ok" onClick={onDone} data-testid="k2-kb-ok">OK</button>;
          return <button key={k} type="button" className="k2-kb-key" onClick={() => onKey(k)}>{k.toLocaleUpperCase('fr')}</button>;
        })}
      </div>
    ))}
  </div>
);

const AccessBtn = () => null;

export default function BorneClubV2({ api, poste, cfg, mode, onBack, onGuest, onIdentified, track }) {
  const [step, setStep] = useState(mode === 'ident' ? 'phone' : 'welcome');
  const [digits, setDigits] = useState('');
  const [otp, setOtp] = useState('');
  const [otpState, setOtpState] = useState('idle');
  const [hint, setHint] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [su, setSu] = useState({ prenom: '', terms: false, sms: false, kb: false });
  const [termsOpen, setTermsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const verifying = useRef(false);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (step !== 'welcome') return undefined;
    const t = setTimeout(() => { track('refuse', 'welcome_screen_abandoned', 0, 'welcome'); onBack(); }, (cfg.veille_s || 40) * 1000);
    return () => clearTimeout(t);
  }, [step, cfg.veille_s, onBack, track]);

  useEffect(() => {
    if (step === 'welcome') return undefined;
    let t;
    const arm = () => { clearTimeout(t); t = setTimeout(() => onBack(), ((cfg.inactivite_s || 60) + (cfg.rebours_s || 15)) * 1000); };
    ['pointerdown', 'keydown'].forEach((e) => document.addEventListener(e, arm, { passive: true }));
    arm();
    return () => { clearTimeout(t); ['pointerdown', 'keydown'].forEach((e) => document.removeEventListener(e, arm)); };
  }, [step, cfg.inactivite_s, cfg.rebours_s, onBack]);

  const guest = () => { track('propose', 'guest_order_clicked', 0, 'welcome'); track('accepte', 'catalog_opened', 0, 'welcome'); onGuest(); };

  const sendOtp = useCallback(async () => {
    try {
      const r = await axios.post(`${api}/borne/club/v2/otp/send`, { phone: digits, poste: String(poste) });
      setResendAt(Date.now() + (r.data.retry_in || cfg.otp_renvoi_s || 30) * 1000);
      setHint('Ce code vérifie que c\'est bien toi.');
      return true;
    } catch (e) {
      setHint(e?.response?.data?.detail || 'Envoi impossible, réessayez');
      if (e?.response?.status === 429) setResendAt(Date.now() + 5000);
      return false;
    }
  }, [api, digits, poste, cfg.otp_renvoi_s]);

  const submitPhone = async () => {
    if (!isMobile(digits) || busy) return;
    setBusy(true); setHint('');
    try {
      const r = await axios.post(`${api}/borne/club/v2/lookup`, { phone: digits, poste: String(poste) });
      if (r.data.known) {
        track('propose', 'club_login_clicked', 0, 'welcome');
        if (cfg.otp_actif) {
          setOtp(''); setOtpState('idle');
          await sendOtp();
          setStep('otp');
        } else {
          const l = await axios.post(`${api}/borne/club/v2/login-direct`, { phone: digits, poste: String(poste) });
          track('accepte', 'club_login_success', 0, 'welcome'); track('accepte', 'catalog_opened', 0, 'welcome');
          onIdentified(l.data.token, l.data.profil, digits);
        }
      } else {
        track('propose', 'club_signup_clicked', 0, 'welcome');
        setStep('signup');
      }
    } catch (e) { setHint(e?.response?.data?.detail || 'Vérification impossible, réessayez'); }
    setBusy(false);
  };

  const verify = useCallback(async (code) => {
    if (verifying.current) return;
    verifying.current = true;
    setOtpState('checking');
    try {
      const r = await axios.post(`${api}/borne/club/v2/otp/verify`, { phone: digits, code, poste: String(poste) });
      setOtpState('success'); setHint('C\'est bien toi !');
      track('accepte', 'club_login_success', 0, 'welcome'); track('accepte', 'catalog_opened', 0, 'welcome');
      setTimeout(() => onIdentified(r.data.token, r.data.profil, digits), 450);
    } catch (e) {
      const st = e?.response?.status;
      setOtp('');
      if (st === 429 || st === 410) { setOtpState('locked'); setHint(st === 410 ? 'Code expiré. Renvoie un code.' : 'Trop d\'essais. Renvoie un code.'); }
      else { setOtpState('error'); setHint(e?.response?.data?.detail || 'Code incorrect.'); }
    }
    verifying.current = false;
  }, [api, digits, poste, onIdentified, track]);

  const typeOtp = (d) => {
    if (['checking', 'success', 'locked'].includes(otpState)) return;
    const v = (otp + d).slice(0, 4);
    setOtp(v);
    if (v.length === 4) verify(v);
  };

  const resend = async () => {
    if (Date.now() < resendAt) return;
    setOtp(''); setOtpState('idle');
    if (await sendOtp()) setHint('Nouveau code envoyé.');
  };

  const submitSignup = async () => {
    if (!su.terms || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${api}/borne/club/v2/register`, {
        phone: digits, prenom: su.prenom.trim(), consent_reglement: true, consent_sms: su.sms, poste: String(poste) });
      track('accepte', 'club_signup_completed', 0, 'welcome'); track('accepte', 'catalog_opened', 0, 'welcome');
      onIdentified(r.data.token, r.data.profil, digits);
    } catch (e) { setHint(e?.response?.data?.detail || 'Inscription impossible, réessayez'); }
    setBusy(false);
  };

  const left = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const back = () => { setDigits(''); setOtp(''); setOtpState('idle'); setHint(''); setStep(mode === 'ident' ? 'phone' : 'welcome'); };

  return (
    <div className={'k2-root'} data-testid="clubv2-root">
      {step === 'welcome' && (
        <div className="k2-screen k2-welcome" data-testid="clubv2-welcome">
          <div className="k2-welcome-visuel" style={{ backgroundImage: 'url(/promos/welcome_club_haut.webp)' }} />
          <div className="k2-welcome-btns">
            <button type="button" className="k2-btn k2-btn-primary" onClick={() => { track('propose', 'club_login_clicked', 0, 'welcome'); setStep('phone'); }} data-testid="clubv2-compte-btn">
              J'ai un compte Boufi Club <span>›</span>
            </button>
            <button type="button" className="k2-btn k2-btn-cream" onClick={guest} data-testid="clubv2-guest-btn">
              🛍 Commander maintenant <span>›</span>
            </button>
          </div>
        </div>
      )}
      {step === 'phone' && (
        <div className="k2-screen k2-ident" data-testid="clubv2-phone">
          <button type="button" className="k2-back" onClick={() => (mode === 'ident' ? onGuest() : back())} data-testid="clubv2-back">‹ Retour</button>
          <div className="k2-head">
            <h1>Ton numéro, c'est tout.</h1>
            <p>Connecte-toi ou rejoins le Club.</p>
          </div>
          <div className={`k2-teldisplay${digits.length === 10 && !isMobile(digits) ? ' is-error' : ''}`} data-testid="clubv2-tel-display">
            {fmtTel(digits) || <span className="k2-ph">06 ·· ·· ·· ··</span>}
          </div>
          <p className={`k2-hint${digits.length === 10 && !isMobile(digits) ? ' is-error' : ''}`}>
            {digits.length === 10 && !isMobile(digits) ? 'Entre un numéro de mobile en 06 ou 07.' : hint || ' '}
          </p>
          <Numpad onDigit={(d) => setDigits((v) => (v + d).slice(0, 10))} onBack={() => setDigits((v) => v.slice(0, -1))} onClear={() => setDigits('')} />
          <button type="button" className="k2-btn k2-btn-primary k2-cta" disabled={!isMobile(digits) || busy} onClick={submitPhone} data-testid="clubv2-continuer">
            CONTINUER <span>→</span>
          </button>
          <button type="button" className="k2-link" onClick={guest} data-testid="clubv2-sans-compte">Commander sans compte</button>
        </div>
      )}
      {step === 'otp' && (
        <div className="k2-screen k2-ident" data-testid="clubv2-otp">
          <button type="button" className="k2-back" onClick={back} data-testid="clubv2-changer-numero">‹ Changer de numéro</button>
          <div className="k2-head">
            <h1>Ton code SMS</h1>
            <p>Envoyé au <b>{digits.slice(0, 2)} •• •• •• {digits.slice(8)}</b></p>
          </div>
          <div className={`k2-otp is-${otpState}`} data-testid="clubv2-otp-boxes">
            {[0, 1, 2, 3].map((i) => <span key={i} className={otp.length === i ? 'is-cur' : ''}>{otp[i] ? '•' : ''}</span>)}
          </div>
          <p className={`k2-hint${['error', 'locked'].includes(otpState) ? ' is-error' : ''}${otpState === 'success' ? ' is-ok' : ''}`} data-testid="clubv2-otp-hint">
            {hint || 'Ce code vérifie que c\'est bien toi.'}
          </p>
          <Numpad onDigit={typeOtp} onBack={() => setOtp((v) => v.slice(0, -1))} onClear={() => setOtp('')} disabled={['checking', 'success'].includes(otpState)} />
          <button type="button" className="k2-btn k2-btn-outline" disabled={left > 0} onClick={resend} data-testid="clubv2-renvoyer">
            {left > 0 ? `Renvoyer · 0:${String(left).padStart(2, '0')}` : 'Renvoyer le code'}
          </button>
          <button type="button" className="k2-link" onClick={guest} data-testid="clubv2-sans-compte-otp">Commander sans compte</button>
        </div>
      )}
      {step === 'signup' && (
        <div className="k2-screen k2-ident k2-signup" data-testid="clubv2-signup">
          <button type="button" className="k2-back" onClick={back} data-testid="clubv2-changer-numero-su">‹ Changer de numéro</button>
          <div className="k2-head">
            <h1>Bienvenue au Club !</h1>
            <p>Ton premier tampon t'attend dès cette commande.</p>
          </div>
          <div className="k2-recap">📱 <b>{fmtTel(digits)}</b><button type="button" onClick={back}>Modifier</button></div>
          <button type="button" className={`k2-field${su.kb ? ' is-focus' : ''}`} onClick={() => setSu((s) => ({ ...s, kb: !s.kb }))} data-testid="clubv2-prenom-field">
            <span className="k2-field-label">Ton prénom <em>· facultatif</em></span>
            <span className={`k2-field-val${!su.prenom ? ' is-ph' : ''}`}>{su.prenom || 'Pour t\'appeler au comptoir'}</span>
          </button>
          <div className="k2-consents">
            <div className={`k2-consent${su.terms ? ' is-on' : ''}`}>
              <button type="button" role="checkbox" aria-checked={su.terms} onClick={() => setSu((s) => ({ ...s, terms: !s.terms }))} data-testid="clubv2-consent-reglement">
                <span className="k2-check">{su.terms ? '✓' : ''}</span>
                <span>J'accepte le règlement du Boufi Club et la politique de confidentialité.</span>
              </button>
              <button type="button" className="k2-read" onClick={() => setTermsOpen(true)} data-testid="clubv2-lire">Lire</button>
            </div>
            <div className={`k2-consent${su.sms ? ' is-on' : ''}`}>
              <button type="button" role="checkbox" aria-checked={su.sms} onClick={() => setSu((s) => ({ ...s, sms: !s.sms }))} data-testid="clubv2-consent-sms">
                <span className="k2-check">{su.sms ? '✓' : ''}</span>
                <span>Je veux recevoir les nouveautés et les drops par SMS. <em>Facultatif</em></span>
              </button>
            </div>
          </div>
          <p className="k2-fine">Un SMS confirmera ton numéro. Tu peux quitter le Club à tout moment.</p>
          <button type="button" className="k2-btn k2-btn-primary k2-cta" disabled={!su.terms || busy} onClick={submitSignup} data-testid="clubv2-rejoindre">
            Rejoindre et commander <span>→</span>
          </button>
          <button type="button" className="k2-link" onClick={guest} data-testid="clubv2-sans-compte-su">Commander sans compte</button>
          {hint && <p className="k2-hint is-error">{hint}</p>}
          {su.kb && <Keyboard onKey={(k) => setSu((s) => ({ ...s, prenom: (s.prenom + k).slice(0, 30) }))} onBack={() => setSu((s) => ({ ...s, prenom: s.prenom.slice(0, -1) }))} onDone={() => setSu((s) => ({ ...s, kb: false }))} />}
        </div>
      )}
      {termsOpen && (
        <div className="k2-veil" onClick={(e) => e.target === e.currentTarget && setTermsOpen(false)} data-testid="clubv2-terms">
          <div className="k2-sheet">
            <h2>Règlement du Boufi Club</h2>
            <ul>
              <li>1 achat = 1 tampon · 10 tampons = 1 cookie offert.</li>
              <li>Ton numéro sert uniquement à ta fidélité. Rien n'est gardé sur la borne.</li>
              <li>Les SMS de nouveautés sont facultatifs et résiliables à tout moment (STOP).</li>
              <li>Tu peux quitter le Club et faire effacer tes données sur simple demande.</li>
            </ul>
            <div className="k2-qr">
              <QRCodeSVG value={cfg.reglement_url || 'https://bouficookies.fr/club/reglement'} size={190} bgColor="#FFFDFA" fgColor="#2A1013" />
              <p>Scanne avec ton téléphone<br />pour le règlement complet<br /><em>{cfg.reglement_version}</em></p>
            </div>
            <button type="button" className="k2-btn k2-btn-primary" onClick={() => setTermsOpen(false)} data-testid="clubv2-terms-fermer">J'ai compris</button>
          </div>
        </div>
      )}
    </div>
  );
}

export const ClubBarV2 = ({ profil, cfg, onOpen, onGift, giftInCart }) => {
  if (!profil) return null;
  const seuil = cfg?.tampons_cadeau || 10;
  const stamps = Math.min(seuil, profil.stamps ?? 0);
  const cadeau = (profil.stamps ?? 0) >= seuil;
  return (
    <div className="k2-chipzone" data-testid="clubv2-strip">
      <button type="button" className="k2-chip" onClick={onOpen} data-testid="clubv2-strip-open">
        ⭐ {profil.prenom || 'Mon Club'} · {stamps}/{seuil}
      </button>
      {cadeau && !giftInCart && <button type="button" className="k2-chip k2-chip-gift" onClick={onGift} data-testid="clubv2-strip-gift">🎁 Mon cadeau</button>}
    </div>
  );
};

export const MonClubV2 = ({ profil, cfg, onClose, onGift, onLogout, giftInCart }) => {
  const seuil = cfg?.tampons_cadeau || 10;
  const cadeau = (profil.stamps ?? 0) >= seuil;
  return (
    <div className="k2-veil" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="clubv2-monclub">
      <div className="k2-sheet">
        <button type="button" className="k2-close" onClick={onClose} data-testid="clubv2-monclub-fermer">✕</button>
        <h2>Mon Club</h2>
        <p className="k2-mc-id"><b>{profil.prenom}</b> · statut {profil.tier}</p>
        <div className="k2-stamps" data-testid="clubv2-stamps">
          {Array.from({ length: seuil }).map((_, i) => <i key={i} className={i < Math.min(seuil, profil.stamps ?? 0) ? 'on' : ''}>🍪</i>)}
        </div>
        <p className="k2-mc-sub">{cadeau ? 'Cookie offert disponible 🎉' : `Encore ${seuil - (profil.stamps ?? 0)} tampon${seuil - (profil.stamps ?? 0) > 1 ? 's' : ''} avant ton cookie offert`}</p>
        {cadeau && !giftInCart && <button type="button" className="k2-btn k2-btn-primary" onClick={onGift} data-testid="clubv2-monclub-gift">🎁 Utiliser mon cadeau</button>}
        {profil.defi && (
          <div className="k2-mc-card" data-testid="clubv2-defi">
            <b>🏆 Défi du mois — goûte 3 parfums</b>
            <span>{profil.defi.complete ? (profil.defi.utilise ? 'Réussi — récompense déjà utilisée' : 'Réussi ! Ton cookie t\'attend 🎉') : `${profil.defi.n}/3 parfums ce mois-ci`}</span>
          </div>
        )}
        {profil.referral_code && (
          <div className="k2-mc-card" data-testid="clubv2-parrainage">
            <b>🤝 Parrainage</b>
            <span>Ton code : <b className="k2-refcode">{profil.referral_code}</b> — un cookie offert pour toi et ton filleul.</span>
          </div>
        )}
        <button type="button" className="k2-link k2-logout" onClick={onLogout} data-testid="clubv2-logout">Se déconnecter</button>
      </div>
    </div>
  );
};

export const GiftSheetV2 = ({ produits, onPick, onClose, titre = 'Ton cookie offert 🎁', sousTitre = 'Choisis parmi les cookies du moment — il sera à 0 € dans ton panier.' }) => (
  <div className="k2-veil" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="clubv2-gift">
    <div className="k2-sheet">
      <button type="button" className="k2-close" onClick={onClose} data-testid="clubv2-gift-fermer">✕</button>
      <h2>{titre}</h2>
      <p className="k2-mc-sub">{sousTitre}</p>
      <div className="k2-gift-grid">
        {produits.map((p) => (
          <button key={p.nom} type="button" className="k2-gift-item" onClick={() => onPick(p.nom)} data-testid={`clubv2-gift-${p.nom.replace(/\s+/g, '-').toLowerCase()}`}>
            {p.img && <img src={p.img} alt={p.nom} />}
            <span>{p.nom}</span><b>0 €</b>
          </button>
        ))}
      </div>
    </div>
  </div>
);
